/**
 * Central mapping between backend metadata keys and form field names.
 * Edit this map to add/remove meta mappings used for both reading and writing.
 */
export const META_MAP = {
    "BDN|ID": "bdnId",
    "POS|ID": "posId",
    "ENERGY|CONTENT|MJ": "energyContentMj",
    "E|VALUE|GCO2EQ|MJ": "eValue",
    "CO2EQ|WTT": "co2eq",
    "PERCENT|MASS": "percentMass",
    "PERCENT|ENERGY": "percentEnergy",
    "FEU|EF|CH4": "feuEfCh4",
    "FEU|EF|CO2": "feuEfCo2",
    "FEU|EF|N2O": "feuEfN2o",
    "IMO|LCV": "imoLcv",
    "EU|LCV": "euLcv",
    "IMO|CF|CO2": "imoCfCo2",
    "UKMRV|EF|CO2": "ukMrvEfCo2",
    "EUMRV|EF|CO2": "euMrvEfCo2",
    "EUMRV|EF|CH4": "euMrvEfCh4",
    "EUMRV|EF|N2O": "euMrvEfN2o",
    "EUETS|EF|CO2": "euEtsEfCo2",
    "EUETS|EF|CH4": "euEtsEfCh4",
    "EUETS|EF|N2O": "euEtsEfN2o",
    "RWD": "rwd",
    "CSLIP": "cSlip"
};


/**
 * Fallback top-level backend fields to try when metadata entry is not present.
 * Keys are form field names, values are arrays of backend field names to try in order.
 */
export const FALLBACKS = {
    energyContentMj: ["ENERGYCONTENTMJ"],
    eValue: ["EVALUEGCO2EQMJ"],
    percentMass: ["PERCENTMASS", "MassPercent", "mass"],
    percentEnergy: ["PERCENTENERGY"],
    massMt: ["QUANTITY"],
    rwd: ["RWD"],
    cSlip: ["CSLIP"],
    bdnId: ["BDNID"],
    posId: ["POSID"],
    imoCfCo2: ["IMOCFCO2"],
    ukMrvEfCo2: ["UKMRVEFCO2"],
    euMrvEfCo2: ["EUMRVEFCO2"],
    euEtsEfCo2: ["EUETSEFCO2"],
    feuEfCo2: ["FEUEFCO2"],
    feuEfCh4: ["FEUEFCH4"],
    feuEfN2o: ["FEUEFN2O"],
    imoLcv: ["IMOLCV"],
    euLcv: ["EULCV"],
    co2eq: ["CO2EQWTT"]
};


export const FIELD_GROUPS = [
    [
        { name: "Energy Content (MJ)", key: "energyContentMj" },
        { name: "E (gCO2eq/MJ)", key: "eValue" },
        { name: "%Mass", key: "percentMass" },
        { name: "%Energy", key: "percentEnergy" },
    ],
    [
        { name: "CO2eq WtT", key: "co2eq" },
        { name: "RWD", key: "rwd" },
        { name: "CSlip (Y/N)", key: "cSlip" },
    ],
    [
        { name: "IMO LCV (MJ/g)", key: "imoLcv" },
        { name: "IMO CF CO2", key: "imoCfCo2" },
        { name: "UK MRV EF CO2", key: "ukMrvEfCo2" },
    ],
    [{ name: "EU LCV (MJ/g)", key: "euLcv" }],
    [
        { name: "EU MRV EF CO2", key: "euMrvEfCo2", required: true },
        { name: "EU MRV EF CH4", key: "euMrvEfCh4", required: true },
        { name: "EU MRV EF N2O", key: "euMrvEfN2o", required: true },
    ],
    [
        { name: "EU ETS EF CO2", key: "euEtsEfCo2", required: true },
        { name: "EU ETS EF CH4", key: "euEtsEfCh4", required: true },
        { name: "EU ETS EF N2O", key: "euEtsEfN2o", required: true },
    ],
    [
        { name: "FEU EF CO2", key: "feuEfCo2", required: true },
        { name: "FEU EF CH4", key: "feuEfCh4", required: true },
        { name: "FEU EF N2O", key: "feuEfN2o", required: true },
    ],
];

export const FIXED_FIELD_GROUP = [
    { name: "Name", key: "name" },
    { name: "BDN ID", key: "bdnId" },
    { name: "PoS ID", key: "posId" },
    { name: "Mass (MT)", key: "massMt" }
];

export const DEFAULT_VALUES = {
    classificationType: "Biofuel",
    code: "",
    name: "",
    bdnId: "",
    posId: "",
    energyContentMj: "",
    eValue: "",
    co2eq: "",
    percentMass: "",
    percentEnergy: "",
    feuEfCo2: "",
    feuEfCh4: "",
    feuEfN2o: "",
    imoLcv: "",
    euLcv: "",
    imoCfCo2: "",
    ukMrvEfCo2: "",
    euMrvEfCo2: "",
    euMrvEfCh4: "",
    euMrvEfN2o: "",
    euEtsEfCo2: "",
    euEtsEfCh4: "",
    euEtsEfN2o: "",
    rwd: "",
    cSlip: "",
    fuelRegId: 0,
    parentFuelRegId: 0,
    massMt: "",
    childFuels: []
};

export const getFallbackValue = (obj = {}, formField) => {
    const keys = FALLBACKS[formField];
    if (!keys) return undefined;
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

// convert metadata array -> flat fields using META_MAP; apply FALLBACKS from fallbackSource
const metadataArrayToFields = (metadata = [], fallbackSource = {}) => {
    const out = {};
    if (Array.isArray(metadata)) {
        for (const entry of metadata) {
            const metaKey = entry.metaKey ?? entry.MetaKey ?? entry.meta_key;
            const metaValue = entry.metaValue ?? entry.MetaValue ?? entry.value;
            const formField = META_MAP[metaKey];
            if (formField) out[formField] = metaValue;
        }
    }
    // fallbacks for fields not present
    Object.keys(FALLBACKS).forEach((formField) => {
        if (out[formField] === undefined) {
            const v = getFallbackValue(fallbackSource, formField);
            if (v !== undefined) out[formField] = v;
        }
    });
    return out;
};

const metaTypedata = (fuel) =>
    fuel?.metadata?.reduce((acc, meta) => {
        const key = meta.metaKey.replace(/\|/g, "");
        acc[key] = meta.metaType;
        return acc;
    }, {}) || {};

// map top-level backend fuel -> normalized form values (parent + children)
const createFuelRecord = (fuel, parentFuelRegId) => {
    const metaFields = metadataArrayToFields(fuel.metadata || fuel.metaData || [], fuel);
    return {
        _tempId: fuel._tempId || crypto.randomUUID(),
        fuelRegId: fuel.FuelRegId ?? fuel.fuelRegId ?? 0,
        parentFuelRegId: parentFuelRegId ?? fuel.parentFuelRegId ?? fuel.ParentFuelRegId ?? 0,
        classificationType: fuel.FuelCategory ?? (fuel.categoryType === "BLEND" ? "Blend" : fuel.categoryType) ?? fuel.classificationType ?? "Biofuel",
        code: fuel.FuelCode ?? fuel.code ?? "",
        name: fuel.FuelName ?? fuel.name ?? "",
        bdnId: metaFields.bdnId ?? fuel.BDNID ?? fuel.bdnId ?? "",
        posId: metaFields.posId ?? fuel.POSID ?? fuel.posId ?? "",
        energyContentMj: metaFields.energyContentMj ?? fuel.EnergyContent ?? fuel.energyContentMj ?? "",
        eValue: metaFields.eValue ?? fuel.EValue ?? fuel.eValue ?? "",
        co2eq: metaFields.co2eq ?? fuel.CO2eqWtT ?? fuel.co2eq ?? "",
        percentMass: metaFields.percentMass ?? fuel.PERCENTMASS ?? fuel.MassPercent ?? fuel.percentMass ?? "",
        percentEnergy: metaFields.percentEnergy ?? fuel.PERCENTENERGY ?? fuel.percentEnergy ?? "",
        feuEfCo2: metaFields.feuEfCo2 ?? fuel.FEUEFCO2 ?? fuel.feuEfCo2 ?? "",
        feuEfCh4: metaFields.feuEfCh4 ?? fuel.FEUEFCH4 ?? fuel.feuEfCh4 ?? "",
        feuEfN2o: metaFields.feuEfN2o ?? fuel.FEUEFN2O ?? fuel.feuEfN2o ?? "",
        imoLcv: metaFields.imoLcv ?? fuel.IMOLCV ?? fuel.imoLcv ?? "",
        euLcv: metaFields.euLcv ?? fuel.EULCV ?? fuel.euLcv ?? "",
        imoCfCo2: metaFields.imoCfCo2 ?? fuel.IMOCFCO2 ?? fuel.imoCfCo2 ?? "",
        ukMrvEfCo2: metaFields.ukMrvEfCo2 ?? fuel.UKMRVEFCO2 ?? fuel.ukMrvEfCo2 ?? "",
        euMrvEfCo2: metaFields.euMrvEfCo2 ?? fuel.EUMRVEFCO2 ?? fuel.euMrvEfCo2 ?? "",
        euMrvEfCh4: metaFields.euMrvEfCh4 ?? fuel.EUMRVEFCH4 ?? fuel.euMrvEfCh4 ?? "",
        euMrvEfN2o: metaFields.euMrvEfN2o ?? fuel.EUMRVEFN2O ?? fuel.euMrvEfN2o ?? "",
        euEtsEfCo2: metaFields.euEtsEfCo2 ?? fuel.EUETSEFCO2 ?? fuel.euEtsEfCo2 ?? "",
        euEtsEfCh4: metaFields.euEtsEfCh4 ?? fuel.EUETSEFCH4 ?? fuel.euEtsEfCh4 ?? "",
        euEtsEfN2o: metaFields.euEtsEfN2o ?? fuel.EUETSEFN2O ?? fuel.euEtsEfN2o ?? "",
        rwd: metaFields.rwd ?? fuel.RWD ?? fuel.rwd ?? "",
        cSlip: metaFields.cSlip ?? fuel.CSLIP ?? fuel.cSlip ?? "",
        massMt: metaFields.massMt ?? fuel.Quantity ?? fuel.massMt ?? "",
        metaTypeMap: fuel.MetaTypeMap || metaTypedata(fuel)
    };
};

export const mapFuelToFormValues = (fuel = {}) => {
    var parentFuel = createFuelRecord(fuel, 0);
    parentFuel.childFuels = (fuel.subRows || fuel.childFuels || []).map((c) => createFuelRecord(c, fuel.fuelRegId ?? fuel.FuelRegId ?? 0));
    return parentFuel;
};